import { apiRequest } from '../../../shared/services/apiClient';
import type {
  FieldAttendanceEvent,
  FieldAttendanceEventListQuery,
  FieldAttendanceStatus,
  FieldCheckInRequest,
  FieldCheckOutRequest,
  FieldDestination,
  FieldLivePresence,
  FieldLocation,
  FieldOperationsSettings,
  FieldTodayJob,
  PagedFieldAttendanceEvents,
  UpdateFieldOperationsSettingsRequest,
} from '../types/FieldOperations';

const BASE = '/api/v1/field-operations';

const pickArray = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { items?: unknown[] }).items)) {
    return (payload as { items: T[] }).items;
  }

  return [];
};

const toQueryString = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

const optionalNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const optionalId = (value: unknown): string | undefined => {
  const text = value ? String(value) : '';
  return text && text !== '00000000-0000-0000-0000-000000000000' ? text : undefined;
};

const DEVICE_ID_KEY = 'simaizeer.deviceId';

export const getFieldDeviceId = (): string => {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY)?.trim();
    if (existing) {
      return existing;
    }
    const created = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `web-${Date.now().toString(36)}`;
    window.localStorage.setItem(DEVICE_ID_KEY, created);
    return created;
  } catch {
    return `web-${Date.now().toString(36)}`;
  }
};

const optionalText = (value: unknown): string | undefined => {
  const text = value ? String(value).trim() : '';
  return text || undefined;
};

const normalizeLocation = (value: any): FieldLocation => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  code: value?.code ? String(value.code) : undefined,
  latitude: optionalNumber(value?.latitude),
  longitude: optionalNumber(value?.longitude),
  checkInRadiusMeters: optionalNumber(value?.checkInRadiusMeters),
  isPrimary: Boolean(value?.isPrimary),
  isActive: value?.isActive !== false,
});

const normalizeDestination = (value: any): FieldDestination => ({
  targetKind: String(value?.targetKind ?? 'location'),
  locationId: optionalId(value?.locationId),
  calendarEventId: optionalId(value?.calendarEventId),
  taskId: optionalId(value?.taskId),
  label: String(value?.label ?? ''),
  address: value?.address ? String(value.address) : undefined,
  latitude: optionalNumber(value?.latitude),
  longitude: optionalNumber(value?.longitude),
  radiusMeters: optionalNumber(value?.radiusMeters),
});

const normalizeEvent = (value: any): FieldAttendanceEvent => ({
  id: String(value?.id ?? ''),
  employeeId: String(value?.employeeId ?? ''),
  employeeName: String(value?.employeeName ?? ''),
  destination: normalizeDestination(value?.destination),
  kind: String(value?.kind ?? ''),
  occurredAt: String(value?.occurredAt ?? ''),
  latitude: optionalNumber(value?.latitude),
  longitude: optionalNumber(value?.longitude),
  accuracyMeters: optionalNumber(value?.accuracyMeters),
  distanceMeters: optionalNumber(value?.distanceMeters),
  source: String(value?.source ?? 'web'),
  deviceId: optionalText(value?.deviceId),
  notes: value?.notes ? String(value.notes) : undefined,
  pairId: optionalId(value?.pairId),
});

const normalizePresence = (value: any): FieldLivePresence => ({
  employeeId: String(value?.employeeId ?? ''),
  employeeName: String(value?.employeeName ?? ''),
  destination: normalizeDestination(value?.destination),
  checkInEventId: String(value?.checkInEventId ?? ''),
  checkedInAt: String(value?.checkedInAt ?? ''),
  latitude: optionalNumber(value?.latitude),
  longitude: optionalNumber(value?.longitude),
  accuracyMeters: optionalNumber(value?.accuracyMeters),
  distanceMeters: optionalNumber(value?.distanceMeters),
  source: String(value?.source ?? 'web'),
  deviceId: optionalText(value?.deviceId),
  notes: value?.notes ? String(value.notes) : undefined,
});

const normalizeTodayJob = (value: any): FieldTodayJob => ({
  targetKind: String(value?.targetKind ?? ''),
  targetId: String(value?.targetId ?? ''),
  title: String(value?.title ?? ''),
  subtitle: value?.subtitle ? String(value.subtitle) : undefined,
  startAtUtc: value?.startAtUtc ? String(value.startAtUtc) : undefined,
  endAtUtc: value?.endAtUtc ? String(value.endAtUtc) : undefined,
  destination: normalizeDestination(value?.destination),
});

const normalizeSettings = (value: any): FieldOperationsSettings => ({
  id: String(value?.id ?? ''),
  isEnabled: Boolean(value?.isEnabled),
  allowLocation: value?.allowLocation !== false,
  allowAdHoc: Boolean(value?.allowAdHoc),
  allowCalendarEvent: Boolean(value?.allowCalendarEvent),
  allowTask: Boolean(value?.allowTask),
  mobileGpsRequired: value?.mobileGpsRequired !== false,
  webGpsRequired: Boolean(value?.webGpsRequired),
  enforceGeofenceOnCheckIn: value?.enforceGeofenceOnCheckIn !== false,
  defaultRadiusMeters: optionalNumber(value?.defaultRadiusMeters) ?? 150,
});

export const getFieldOperationsSettings = async (): Promise<FieldOperationsSettings> =>
  normalizeSettings(await apiRequest<any>(`${BASE}/settings`));

export const updateFieldOperationsSettings = async (
  request: UpdateFieldOperationsSettingsRequest,
): Promise<FieldOperationsSettings> =>
  normalizeSettings(await apiRequest<any>(`${BASE}/settings`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  }));

export const listFieldLocations = async (): Promise<FieldLocation[]> => {
  const payload = await apiRequest<unknown>(`${BASE}/locations`);
  return pickArray<any>(payload).map(normalizeLocation).filter((item) => item.id);
};

export const listFieldTodayJobs = async (): Promise<FieldTodayJob[]> => {
  const payload = await apiRequest<unknown>(`${BASE}/today-jobs`);
  return pickArray<any>(payload).map(normalizeTodayJob).filter((item) => item.targetId);
};

export const getMyFieldStatus = async (): Promise<FieldAttendanceStatus> => {
  const payload = await apiRequest<any>(`${BASE}/me/status`);
  const openVisit = payload?.openVisit ? normalizePresence(payload.openVisit) : null;
  return { openVisit };
};

export const checkInField = async (request: FieldCheckInRequest): Promise<FieldAttendanceEvent> => {
  const payload = await apiRequest<unknown>(`${BASE}/check-in`, {
    method: 'POST',
    body: JSON.stringify({
      ...request,
      source: request.source ?? 'web',
      deviceId: request.deviceId ?? getFieldDeviceId(),
    }),
  });
  return normalizeEvent(payload);
};

export const checkOutField = async (request: FieldCheckOutRequest): Promise<FieldAttendanceEvent> => {
  const payload = await apiRequest<unknown>(`${BASE}/check-out`, {
    method: 'POST',
    body: JSON.stringify({
      ...request,
      source: request.source ?? 'web',
      deviceId: request.deviceId ?? getFieldDeviceId(),
    }),
  });
  return normalizeEvent(payload);
};

export const listLiveFieldPresence = async (): Promise<FieldLivePresence[]> => {
  const payload = await apiRequest<unknown>(`${BASE}/live`);
  return pickArray<any>(payload).map(normalizePresence).filter((item) => item.employeeId);
};

export const listFieldAttendanceEvents = async (
  query: FieldAttendanceEventListQuery = {},
): Promise<PagedFieldAttendanceEvents> => {
  const payload = await apiRequest<any>(`${BASE}/events${toQueryString({
    employeeId: query.employeeId,
    locationId: query.locationId,
    targetKind: query.targetKind,
    fromUtc: query.fromUtc,
    toUtc: query.toUtc,
    kind: query.kind,
    page: query.page,
    pageSize: query.pageSize,
  })}`);

  const items = pickArray<any>(payload).map(normalizeEvent).filter((item) => item.id);
  return {
    items,
    page: Number(payload?.page ?? query.page ?? 1),
    pageSize: Number(payload?.pageSize ?? query.pageSize ?? items.length),
    total: Number(payload?.total ?? items.length),
    hasNext: Boolean(payload?.hasNext),
  };
};

export const captureBrowserGps = (): Promise<{ latitude: number; longitude: number; accuracyMeters?: number } | null> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : undefined,
        });
      },
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
    );
  });
};
